import { useEffect, useState } from 'react';
import type {
  ConfigurationFormField,
  ConfigurationPrompt,
  ConfigurationTemplateFields,
} from 'src/services/configurationService';
import type { Workflow } from 'src/pages/ConfigurationPage/WorkflowsTab/workflowFormValues';
import type { PersonaInputData } from 'src/pages/ConfigurationPage/utils/getPersonaFormValues';

// A template field with its position in the template's field list.
export type IndexedFormField<Value = unknown> = ConfigurationFormField<Value> & { index: number };

// A field as stored in ConfigurationFormFieldsObject: under its own key.
type KeyedField<Key extends string, Value = unknown> = IndexedFormField<Value> & { key: Key };

// The active version's fields by key. Persona fields are listed with their key and, where the
// page reads it, their default value type; any other field is reachable by key.
export interface ConfigurationFormFieldsObject {
  pf_ai_name?: KeyedField<'pf_ai_name'>;
  pf_company_name?: KeyedField<'pf_company_name'>;
  pf_company_description?: KeyedField<'pf_company_description'>;
  pf_pages?: KeyedField<'pf_pages'>;
  pf_role?: KeyedField<'pf_role', string>;
  pf_role_custom?: KeyedField<'pf_role_custom'>;
  pf_cta?: KeyedField<'pf_cta'>;
  pf_icp?: KeyedField<'pf_icp'>;
  pf_tone_of_voice_channel?: KeyedField<'pf_tone_of_voice_channel', string>;
  pf_tone_of_voice_style?: KeyedField<'pf_tone_of_voice_style', string>;
  pf_tone_of_voice_style_custom?: KeyedField<'pf_tone_of_voice_style_custom'>;
  pf_tone_of_voice_emoji?: KeyedField<'pf_tone_of_voice_emoji', boolean>;
  pf_tone_of_voice_resp_len?: KeyedField<'pf_tone_of_voice_resp_len', string>;
  pf_default_language?: KeyedField<'pf_default_language', string>;
  pf_excluded_languages?: KeyedField<'pf_excluded_languages'>;
  pf_words_blacklist?: KeyedField<'pf_words_blacklist'>;
  pf_default_workflow?: KeyedField<'pf_default_workflow', Workflow>;
  pf_custom_workflows?: KeyedField<'pf_custom_workflows'>;
  [key: string]: IndexedFormField | undefined;
}

export const withFieldIndex = (fields: ConfigurationFormField[]): IndexedFormField[] =>
  fields.map((field, index) => ({ ...field, index }));

// The fields keyed by their `key`, as the persona and prompt builder forms look them up.
export const toFormFieldsObject = (fields: IndexedFormField[]): ConfigurationFormFieldsObject =>
  fields.reduce((prev: ConfigurationFormFieldsObject, current) => {
    prev[current.key] = current;

    return prev;
  }, {});

// Owns the active version's form-field definitions, its persisted input
// data, and whether it's a free-text "system prompt" version rather than a
// structured persona form.
export const useConfigurationFormFields = ({
  defaultTemplateFields,
  configurationSettingsPrompt,
  hasNoSettingsYet,
}: {
  defaultTemplateFields: ConfigurationTemplateFields | undefined;
  configurationSettingsPrompt: ConfigurationPrompt | undefined;
  hasNoSettingsYet: boolean;
}) => {
  const [formFields, setFormFields] = useState<IndexedFormField[] | null>(null);
  const [inputData, setInputData] = useState<PersonaInputData | null>(null);
  const [systemPromptActive, setSystemPromptActive] = useState(false);
  const [formFieldsObject, setFormFieldsObject] = useState<ConfigurationFormFieldsObject>({});

  const resetToTemplateDefaults = () => {
    if (!defaultTemplateFields) {
      return;
    }

    setFormFields(withFieldIndex(defaultTemplateFields.form_fields));
    setInputData(null);
  };

  // Brand-new project: no versions exist yet, so seed the form with the
  // template's defaults.
  useEffect(() => {
    if (defaultTemplateFields && hasNoSettingsYet) {
      resetToTemplateDefaults();
    }
  }, [defaultTemplateFields, hasNoSettingsYet]);

  useEffect(() => {
    if (configurationSettingsPrompt) {
      if (!configurationSettingsPrompt.is_manual) {
        setSystemPromptActive(false);
        setFormFields(withFieldIndex(configurationSettingsPrompt.form_fields));
        // A non-manual prompt's input_data holds the persona values.
        setInputData(configurationSettingsPrompt.input_data as PersonaInputData);
      } else {
        setSystemPromptActive(true);
      }
    } else if (defaultTemplateFields) {
      resetToTemplateDefaults();
    }
  }, [configurationSettingsPrompt]);

  useEffect(() => {
    if (!formFields) {
      return;
    }

    setFormFieldsObject(toFormFieldsObject(formFields));
  }, [formFields]);

  return {
    formFields,
    resetToTemplateDefaults,
    inputData,
    systemPromptActive,
    setSystemPromptActive,
    formFieldsObject,
  };
};
